import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, X } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import SectionHead from "@/components/site/SectionHead";
import CTASection from "@/components/site/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/i18n";

const PackageDetail = () => {
  const { slug } = useParams();
  const { d, lang } = useLang();
  const p = d.pkg.items[slug];
  const c = d.pkg.common;
  const isMb = p?.area === "mb";

  useEffect(() => {
    if (!p) return;
    const id = "pkg-jsonld";
    document.getElementById(id)?.remove();
    const origin = window.location.origin;
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: p.role ? `${p.name} — ${p.role}` : p.name,
          description: p.sub,
          provider: { "@type": "Person", name: "Cristina Angelini", brand: "Marketing Facile" },
          areaServed: "IT",
          offers: { "@type": "Offer", price: p.priceValue, priceCurrency: "EUR", url: `${origin}/pacchetti/${slug}` },
        },
        {
          "@type": "FAQPage",
          mainEntity: p.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, [slug, lang, p]);

  if (!p) return <Navigate to="/servizi" replace />;

  return (
    <div data-testid={`pkg-page-${slug}`}>
      <section className="bg-paper py-20 lg:py-28" data-testid="pkg-hero">
        <div className="mx-auto grid max-w-7xl items-end gap-12 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-8">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Link
                to={isMb ? "/marketing-branding" : "/fiere-eventi-lanci"}
                data-testid="pkg-back-link"
                className="mb-8 inline-flex items-center gap-2 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink transition-colors duration-300 hover:text-accent"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> {isMb ? c.backMb : c.backFiere}
              </Link>
              <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent">
                <span className="h-px w-10 bg-accent" /> {isMb ? c.areaMb : c.areaFiere}
              </p>
            </motion.div>
            <h1 className="mt-6 max-w-4xl font-serif text-3xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="pkg-headline">
              <MaskedLine delay={0.1}>{p.heroL1}</MaskedLine>
              <MaskedLine delay={0.25}><span className="italic text-accent">{p.heroL2}</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-8 max-w-2xl text-base leading-relaxed text-mutedink sm:text-lg"
            >
              {p.sub}
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="rounded-md border border-ink/12 bg-white p-6 sm:p-8 lg:col-span-4 lg:p-10"
            data-testid="pkg-price-card"
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{p.name}</p>
              {p.role && <p className="font-serif text-lg italic text-ink">{p.role}</p>}
            </div>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{c.investLabel}</p>
            <p className="mt-1 whitespace-nowrap font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="pkg-price">
              {p.price} <span className="font-sans text-base font-normal text-mutedink">{c.vat}</span>
            </p>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">
              {c.durationLabel}: {p.duration}
            </p>
            {p.total && <p className="mt-2 text-xs font-medium text-ink/70" data-testid="pkg-total">{p.total}</p>}
            <p className="mt-4 border-l-2 border-accent pl-4 text-sm leading-relaxed text-mutedink" data-testid="pkg-price-note">{p.priceNote}</p>
            <Link
              to={`/contatti?area=${p.interest}`}
              data-testid="pkg-hero-cta"
              className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-md bg-accent px-7 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
            >
              {p.cta} <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-4 text-center text-xs text-mutedink">{c.ctaNote}</p>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="pkg-problem">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHead overline={c.problemOverline} title={p.problemTitle} />
            </div>
            <Reveal delay={0.15} className="lg:col-span-7">
              <p className="max-w-2xl text-base leading-relaxed text-mutedink sm:text-lg" data-testid="pkg-problem-body">{p.problemBody}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="pkg-forwho">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={c.forOverline} title={p.forTitle} />
          <div className="mt-14 grid gap-px overflow-hidden rounded-md bg-ink/10 sm:grid-cols-2">
            {p.forItems.map((f, i) => (
              <Reveal key={f} delay={i * 0.06} className="flex items-start gap-4 bg-paper p-7 lg:p-9" data-testid={`pkg-forwho-${i}`}>
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-accent" />
                <p className="text-sm leading-relaxed text-ink/85 sm:text-base">{f}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="pkg-goal">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHead overline={c.goalOverline} title={p.goalTitle} />
            </div>
            <Reveal delay={0.15} className="lg:col-span-7">
              <p className="border-l-2 border-accent pl-6 font-serif text-xl italic leading-snug text-ink sm:text-2xl" data-testid="pkg-goal-body">
                {p.goalBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="pkg-includes">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={c.includesOverline} title={p.includesTitle} />
          {p.includesPrefix && (
            <Reveal delay={0.1}>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.12em] text-ink" data-testid="pkg-includes-prefix">{p.includesPrefix}</p>
            </Reveal>
          )}
          <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {p.includesItems.map((f, i) => (
              <Reveal key={f} delay={i * 0.04} className="flex items-start gap-3 rounded-md border border-ink/12 bg-white p-5" data-testid={`pkg-include-${i}`}>
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={2} />
                <span className="text-sm leading-relaxed text-ink/85">{f}</span>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="pkg-work">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={c.workOverline} title={p.workTitle} />
          <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-md bg-ink/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="pkg-work-steps">
            {p.workSteps.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.06} className="group bg-white p-7 transition-colors duration-300 hover:bg-ink lg:p-8" data-testid={`pkg-work-${i}`}>
                <p className="font-serif text-4xl text-outline transition-colors duration-300 group-hover:text-accent group-hover:[-webkit-text-stroke:0]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-5 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-ink transition-colors duration-300 group-hover:text-paper">
                  {s.t}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-mutedink transition-colors duration-300 group-hover:text-white/65">{s.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="pkg-scope">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={c.scopeOverline} title={c.scopeTitle} />
          <div className="mt-14 grid gap-px overflow-hidden rounded-md bg-ink/10 lg:grid-cols-2">
            <div className="bg-paper p-8 lg:p-10" data-testid="pkg-included">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{c.includedTitle}</p>
              <ul className="mt-6 space-y-3">
                {p.included.map((f) => (
                  <li key={f} className="flex gap-3 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={2} />
                    <span className="leading-relaxed text-ink/85">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-ink p-8 text-paper lg:p-10" data-testid="pkg-excluded">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent-bright">{c.excludedTitle}</p>
              <ul className="mt-6 space-y-3">
                {p.excluded.map((f) => (
                  <li key={f} className="flex gap-3 text-sm">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-accent-bright" strokeWidth={2} />
                    <span className="leading-relaxed text-white/75">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Reveal delay={0.2} className="mt-10">
            <p className="max-w-3xl border-l-2 border-accent pl-5 text-sm font-medium leading-relaxed text-ink" data-testid="pkg-scope-note">
              {p.scopeNote}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="pkg-diff">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={c.diffOverline} title={p.diffTitle} lead={p.diffLead} />
          <div className="mt-16 grid gap-8 lg:grid-cols-3">
            <Reveal className="flex flex-col rounded-md border-2 border-accent bg-paper p-8 lg:p-10" data-testid="pkg-diff-self">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{c.diffSelfLabel}</p>
              <p className="mt-3 font-serif text-3xl text-ink">{p.name}</p>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-mutedink">{p.diffSelf}</p>
              <p className="mt-6 border-t border-ink/10 pt-5 font-serif text-2xl text-ink">
                {p.price} <span className="font-sans text-xs font-normal text-mutedink">{c.vat}</span>
              </p>
            </Reveal>
            {p.diffOthers.map((o, i) => (
              <Reveal key={o.name} delay={(i + 1) * 0.08} className="flex flex-col rounded-md border border-ink/12 bg-paper p-8 transition-colors duration-300 hover:border-accent lg:p-10" data-testid={`pkg-diff-other-${i}`}>
                <p className="font-serif text-3xl text-ink">{o.name}</p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-mutedink">{o.line}</p>
                <Link
                  to={o.href}
                  data-testid={`pkg-diff-link-${i}`}
                  className="mt-6 inline-flex items-center gap-2 self-start py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-accent transition-colors duration-300 hover:text-accent-dark"
                >
                  {c.detailCta} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="pkg-faq">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <SectionHead overline={c.faqOverline} title={p.faqTitle} />
            </div>
            <div className="lg:col-span-8">
              <Reveal>
                <Accordion type="single" collapsible className="w-full">
                  {p.faq.map((item, i) => (
                    <AccordionItem key={i} value={`pkg-faq-${i}`} className="border-ink/10">
                      <AccordionTrigger
                        data-testid={`pkg-faq-trigger-${i}`}
                        className="py-5 text-left font-serif text-xl font-normal text-ink hover:text-accent hover:no-underline"
                      >
                        <span className="flex items-baseline gap-4">
                          <span className="font-mono text-xs text-mutedink">{String(i + 1).padStart(2, "0")}</span>
                          {item.q}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-6 pl-9 text-base leading-relaxed text-mutedink" data-testid={`pkg-faq-content-${i}`}>
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection interest={p.interest} />
    </div>
  );
};

export default PackageDetail;
